import { Assert } from "@seriously-rad/seriouslyrad-framework-core/Assert";
import { Nullable, PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { IJNativeEvent, JNative } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { Property } from "@seriously-rad/seriouslyrad-framework-core/RadObject";
import { ContainerControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ContainerControl";
import { Control, ControlPropertyValues, IControl } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Control";
import { Point } from "@seriously-rad/seriouslyrad-framework-core/Ui/Point";
import { IResizeBandMoveStartArgs, ResizeBand } from "./ResizeBand";
import { NAMESPACE } from "./_Namespace";

export type ResizableControlPropertyValues =
    ControlPropertyValues
    & {
    };

export type ResizableControlProperties = {
    [name in keyof ResizableControlPropertyValues]: Property<ResizableControlPropertyValues[name]>
};

export interface IResizableControl extends IControl, ResizableControlProperties
{
}

export abstract class ResizableControl extends Control implements IResizableControl
{
    // #region Static

    public static override classNamespace = NAMESPACE + "ResizableControl";
    public static override className = "ResizableControl";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ResizableControlPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                minHeight: 0,
                minWidth: 0,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected clickTimeoutId?: number;
    protected resizeBand?: ResizeBand;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ResizableControlPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-resizable-control");
        this.clickTimeoutId = 0;
    }

    // #endregion

    // #region Public Methods

    public deselect()
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        if (!this.selectedGet())
            return;

        this.resizeBand.hide();

        this.trigger("selectionChanged");
    }
    public select(addToSelection: boolean, focus: boolean)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        if (this.selectedGet())
            return;

        if (!this.enabled.get())
            return;

        if (!addToSelection)
            this.resizeBand._clearSelection();

        this.resizeBand.show();

        if (focus)
            this.focus();

        this.trigger("selectionChanged");
    }
    public selectedGet()
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        return this.resizeBand.isVisible.get();
    }
    public startMove(args: IResizeBandMoveStartArgs)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        this.resizeBand.startMove(args);
    }
    public toggleSelect()
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        if (!this.enabled.get())
            return;

        if (this.resizeBand.isVisible.get())
            this.resizeBand.hide();
        else
            this.resizeBand.show();

        this.trigger("selectionChanged");
    }

    // #endregion

    // #region Protected Methods

    protected _moveResizeBandToParentControl()
    {
        const
            parentControl = this.parentControlGet();

        Assert.isInstanceOfClass(ContainerControl, parentControl, "parentControl");

        parentControl.append(this.resizeBand);
    }
    protected _testDoubleClick()
    {
        if (this.clickTimeoutId)
        {
            // Double Click!
            clearTimeout(this.clickTimeoutId);
            this.clickTimeoutId = 0;
            this.trigger("doubleClick");
            return true;
        }

        this.clickTimeoutId = window.setTimeout(() => { this.clickTimeoutId = 0; }, 250);
        return false;
    }

    // #endregion

    // #region Overrides

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        this.on("mousedown touchstart", { self: this }, this.onInputStart);
        this.resizeBand.on("deselected", { self: this }, this.onResizeBandDeselected);
        this.resizeBand.on("inputStart", { self: this }, this.onResizeBandInputStart);
        this.resizeBand.on("moveEnd", { self: this }, this.onResizeBandMoveEnd);
        this.resizeBand.on("resizeEnd", { self: this }, this.onResizeBandResizeEnd);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.resizeBand = new ResizeBand({ associatedControl: this });

        return $$element;
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        if (!change.currentValue)
            this.deselect();
    }
    protected override _heightChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._heightChanged(change);
        this.resizeBand.height.set(change.currentValue);
    }
    protected override _leftChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._leftChanged(change);
        this.resizeBand.left.set(change.currentValue);
    }
    protected override _maxHeightChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._maxHeightChanged(change);
        this.resizeBand.maxHeight.set(change.currentValue);
    }
    protected override _maxWidthChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._maxWidthChanged(change);
        this.resizeBand.maxWidth.set(change.currentValue);
    }
    protected override _minHeightChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._minHeightChanged(change);
        this.resizeBand.minHeight.set(change.currentValue);
    }
    protected override _minWidthChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._minWidthChanged(change);
        this.resizeBand.minWidth.set(change.currentValue);
    }
    protected override _topChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._topChanged(change);
        this.resizeBand.top.set(change.currentValue);
    }
    protected override _widthChanged(change: PropertyChange<number>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super._widthChanged(change);
        this.resizeBand.width.set(change.currentValue);
    }
    public override appendTo($$elementOrContainerControl: Nullable<any[] | ContainerControl | HTMLElement | string | undefined>, beforeSelector?: any)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        super.appendTo($$elementOrContainerControl, beforeSelector);
        this.resizeBand.appendTo($$elementOrContainerControl, beforeSelector);
        return this;
    }
    public override prependTo($$elementOrContainerControl: Nullable<any[] | ContainerControl | HTMLElement | string | undefined>)
    {
        Assert.isNotNullOrUndefined(this.resizeBand, "this.resizeBand");

        this.resizeBand.prependTo($$elementOrContainerControl);
        super.prependTo($$elementOrContainerControl);
        return this;
    }
    public override remove()
    {
        if (this.resizeBand)
        {
            this.resizeBand.cancelMove();
            this.resizeBand.remove();
            delete this.resizeBand;
        }

        super.remove();
    }

    protected override onParentChanged(evt: IJNativeEvent, change: PropertyChange<JNative<HTMLElement>>)
    {
        const
            self = evt.data.self as this;

        self._moveResizeBandToParentControl();
    }

    // #endregion

    // #region Event Handlers

    protected onInputStart(evt: IJNativeEvent<MouseEvent | TouchEvent>)
    {
        const
            self = evt.data.self as this;

        evt.preventDefault();
        evt.stopPropagation();

        if (self._testDoubleClick())
            return;

        self.select(evt.originalEvent.shiftKey || evt.originalEvent.ctrlKey, true);

        self.startMove({
            startPoint: Point.fromEvent(evt),
            currentPoint: Point.fromEvent(evt),
            shiftKey: evt.originalEvent.shiftKey,
            ctrlKey: evt.originalEvent.ctrlKey
        });
    }
    protected onResizeBandDeselected(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.deselect();
        self.trigger("selectionChanged");
    }
    protected onResizeBandInputStart(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        if (self._testDoubleClick())
            eventData.cancel = true;
    }
    protected onResizeBandMoveEnd(evt: IJNativeEvent)
    {
        /* Intentionally empty */
    }
    protected onResizeBandResizeEnd(evt: IJNativeEvent)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion
}